import AsyncCreatableSelect from "react-select/async-creatable";
import { useCallback, useMemo } from "react";
import queryString from "query-string";
import { useAuth } from "../hooks/useAuth";
import { useStoreState } from "easy-peasy";
import { Col, Row } from "react-bootstrap";
import { UserSolidIcon } from "./Icons";

const toOption = customer => ({
  value: customer?.Cust_ID,
  label: customer?.LastName,
  customer,
});

export default function CustomerSelectInput({
  walkInCustomer,
  selectedCustomer,
  setSelectedCustomer,
  withCredit = false,
  withPaymentDue = false,
  withInvoiceCat = false,
  subscriptionActive = false,
  placeholder = "Search customer…",
  isDisabled = false,
  onCreateCustomer,
}) {
  const { backendUrl, token, user: authUser } = useAuth();
  const generalSettings = useStoreState(state => state.generalSettings);

  const walkInOption = useMemo(() => toOption(walkInCustomer), [
    walkInCustomer,
  ]);

  // ─── Fetch helpers ────────────────────────────────────────────────────────

  const buildParams = useCallback(
    (search = "", page = 1, limit = 100) => ({
      page,
      limit,
      LastName: search,
      withCredit,
      withPaymentDue,
      withInvoiceCat,
      subscriptionActive,
      Staff_ID:
        authUser?.invexVersion === "InvexInvoicing" ? authUser?.Staff_ID : "",
    }),
    [authUser, withCredit, withPaymentDue, withInvoiceCat, subscriptionActive]
  );

  const fetchCustomers = useCallback(
    async (search = "") => {
      const params = buildParams(search, 1, search ? 50 : 100);

      const response = await fetch(
        `${backendUrl}/api/customers?${queryString.stringify(params)}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message);
      }

      const { data } = await response.json();
      return data.customers || [];
    },
    [backendUrl, token, buildParams]
  );

  // ─── Load options ─────────────────────────────────────────────────────────

  const loadOptions = useCallback(
    async inputValue => {
      try {
        const customers = await fetchCustomers(inputValue);
        const options = customers.map(toOption);

        if (!inputValue) {
          return [walkInOption, ...options];
        }
        return options;
      } catch (error) {
        console.error("CustomerSelectInput: failed to load customers", error);
        return [];
      }
    },
    [fetchCustomers, walkInOption]
  );

  // ─── Change handler ───────────────────────────────────────────────────────

  const handleChange = (selected, { action }) => {
    if (action === "clear" || !selected) {
      setSelectedCustomer(walkInCustomer);
      return;
    }

    if (selected.__isNew__) {
      if (onCreateCustomer) {
        onCreateCustomer(selected.value);
      }
      return;
    }

    setSelectedCustomer(selected.customer);
  };

  // ─── Current value ────────────────────────────────────────────────────────

  const currentValue =
    selectedCustomer?.Cust_ID &&
    selectedCustomer?.Cust_ID !== walkInCustomer?.Cust_ID
      ? toOption(selectedCustomer)
      : walkInOption;
  console.log(
    selectedCustomer && selectedCustomer?.Cust_ID !== walkInCustomer?.Cust_ID
  );

  // ─── Option label — name only, no ID ─────────────────────────────────────

  const formatOptionLabel = ({ customer, __isNew__, label }) => {
    if (__isNew__) {
      return (
        <span className="customer-select__create-label">
          Create new customer: <strong>{label}</strong>
        </span>
      );
    }

    if (!customer) return <span>{label}</span>;

    const showFirstName =
      generalSettings?.enableCustomerSubscriptionActivation &&
      customer.FirstName;

    // Full name — FirstName (if enabled) + LastName
    const fullName = showFirstName
      ? `${customer.FirstName} ${customer.LastName}`
      : customer.LastName;

    return (
      <div className="">
        <div>
          <div className="customer-select__option-name">
            <Col span={12}>
              <b>{fullName}</b>
            </Col>
          </div>
          <div className="customer-select__option-name">
            <Col span={12}>
              <small className="me-2">{customer?.Cust_ID}</small>
            </Col>
          </div>
        </div>
      </div>
    );

    // <span className="customer-select__option-name">{fullName}</span>;
  };

  // ─── Styles ───────────────────────────────────────────────────────────────

  const customStyles = {
    control: (base, state) => ({
      ...base,
      width: "100%",
      minWidth: 0,
      minHeight: "38px",
      borderColor: state.isFocused ? "#0a4a95" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.1rem rgba(13,110,253,.25)" : "none",
      //   "&:hover": { borderColor: "#2463ae" },
    }),
    option: (base, { isSelected, isFocused }) => ({
      ...base,
      backgroundColor: isSelected ? "#0d6efd" : isFocused ? "#e8f0fe" : "white",
      color: isSelected ? "white" : "#212529",
      padding: "6px 12px",
    }),
    menu: base => ({
      ...base,
      zIndex: 9999,
    }),
    container: base => ({
      ...base,
      width: "100%",
      flex: 1,
      minWidth: 0,
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="d-flex  align-items-center gap-3 w-100">
      <div className="avatar">
        <UserSolidIcon />
      </div>
      <div style={{ width: "100%" }}>
        <AsyncCreatableSelect
          classNamePrefix="customer-select"
          styles={customStyles}
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          value={currentValue}
          onChange={handleChange}
          formatOptionLabel={formatOptionLabel}
          placeholder={placeholder}
          isClearable
          isDisabled={isDisabled}
          getOptionValue={opt => opt.value}
          getOptionLabel={opt => opt.label}
          formatCreateLabel={inputValue =>
            `Create new customer: "${inputValue}"`
          }
          loadingMessage={() => "Loading customers…"}
          noOptionsMessage={({ inputValue }) =>
            inputValue
              ? `No customer found for "${inputValue}"`
              : "No customers"
          }
        />
      </div>
    </div>
  );
}
